import {
  createFileRoute,
  Link,
  redirect,
} from '@tanstack/react-router';
import {
  getOrganizerById,
  checkOrganizerEditAccess,
} from '@/server/organizer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Navbar } from '@/components/ui/navbar';
import { EditOrganizerSection } from '@/components/adminEditOrganizer/adminEditOrganizer';
import { OrganizerClaimSection } from '@/components/adminEditOrganizer/organizerClaimSection';

export const Route = createFileRoute('/admin/organizer/$organizerId')({
  component: AdminOrganizerPage,
  beforeLoad: async ({ context, location, params }) => {
    if (!context.profile) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }

    const { hasAccess } = await checkOrganizerEditAccess({
      data: params.organizerId,
    });
    if (!hasAccess) {
      throw redirect({
        to: '/organizer/$organizerId',
        params: { organizerId: params.organizerId },
        search: { unauthorized: true },
      });
    }

    return { isSuperAdmin: context.profile.level === 'superadmin' };
  },
  loader: async ({ params, context }) => {
    const organizer = await getOrganizerById({
      data: params.organizerId,
    });
    return { organizer, isSuperAdmin: context.isSuperAdmin };
  },
  errorComponent: ({ error }) => {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="p-6 text-center">
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Organizer Not Found
            </h1>
            <p className="text-gray-600 mb-4">
              {error?.message ||
                "The organizer you're looking for doesn't exist."}
            </p>
            <Link to="/search">
              <Button>Browse Events</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  },
});

function AdminOrganizerPage() {
  const { organizer, isSuperAdmin } = Route.useLoaderData();

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link
            to="/organizer/$organizerId"
            params={{ organizerId: organizer.id }}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back to Organizer</span>
          </Link>
          <span className="text-sm font-medium text-gray-500">
            Admin Edit
          </span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <EditOrganizerSection organizer={organizer} />
        {isSuperAdmin && (
          <OrganizerClaimSection
            organizerId={organizer.id}
            totalAdmins={organizer.totalAdmins}
          />
        )}
      </div>
    </div>
  );
}
